import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Truck, Loader2, ArrowLeft, Package } from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import OrderTrackingCard, { ORDER_STAGES } from "../components/dealer/OrderTrackingCard";

/**
 * Händler → EU-Bestellungen: Liste aller Bestellfahrzeuge (stock_state=bestell)
 * mit Liefer-/Status-Nachverfolgung.
 */
export default function DealerOrdersPage() {
    const { user, loading: authLoading } = useAuth();
    const navigate = useNavigate();
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (!authLoading && !(user?.role === "b2b" || user?.employer_dealer_id)) navigate("/");
    }, [user, authLoading, navigate]);

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/dealer/order-tracking");
            setItems(data.items || []);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Bestellungen nicht ladbar");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (user?.role === "b2b" || user?.employer_dealer_id) load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user]);

    if (authLoading) {
        return <div className="flex items-center justify-center min-h-[40vh]"><Loader2 className="w-6 h-6 animate-spin text-gray-400" /></div>;
    }

    const stageLabel = (k) => (ORDER_STAGES.find((s) => s.key === k)?.label || "Bestellt");

    return (
        <div className="max-w-4xl mx-auto px-4 py-8" data-testid="dealer-orders-page">
            <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                    <Truck className="w-7 h-7 text-[#FFCC00]" />
                    <div>
                        <h1 className="font-display text-2xl font-bold">EU-Bestellungen</h1>
                        <p className="text-sm text-gray-600">Liefer- &amp; Status-Nachverfolgung Ihrer Bestellfahrzeuge.</p>
                    </div>
                </div>
                <Link to="/dealer/disposition" className="inline-flex items-center gap-2 text-sm font-bold text-gray-700 hover:text-[#111827]">
                    <ArrowLeft className="w-4 h-4" /> Disposition
                </Link>
            </div>

            {loading ? (
                <div className="flex items-center justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-gray-400" /></div>
            ) : items.length === 0 ? (
                <div className="bg-white border border-gray-200 rounded-sm p-8 text-center" data-testid="dealer-orders-empty">
                    <Package className="w-10 h-10 text-gray-300 mx-auto mb-3" />
                    <p className="text-sm text-gray-500 mb-3">Noch keine Bestellfahrzeuge.</p>
                    <Link to="/create-listing?mode=bestellung" className="inline-flex items-center gap-1.5 text-sm font-bold text-[#111827] hover:underline">
                        <Truck className="w-4 h-4" /> Bestellung anlegen
                    </Link>
                </div>
            ) : (
                <div className="space-y-5">
                    {items.map((it) => (
                        <div key={it.id} className="space-y-2" data-testid={`dealer-order-${it.id}`}>
                            <div className="flex items-center justify-between gap-3 flex-wrap">
                                <div className="min-w-0">
                                    <div className="font-bold text-[#111827] truncate">
                                        {it.title || `${it.brand || ""} ${it.model || ""}`.trim() || "Bestellfahrzeug"}
                                    </div>
                                    <div className="text-[11px] text-gray-500">
                                        {it.dispo_order_code ? `Bestellcode ${it.dispo_order_code} · ` : ""}
                                        Status: <b>{stageLabel(it.order_tracking?.status)}</b>
                                        {it.order_tracking?.eta ? ` · ETA ${new Date(it.order_tracking.eta).toLocaleDateString("de-DE")}` : ""}
                                    </div>
                                </div>
                                <Link to={`/create-listing/${it.id}`} className="text-xs font-bold text-gray-500 hover:text-[#111827]">Inserat öffnen</Link>
                            </div>
                            <OrderTrackingCard listing={it} onUpdated={() => load()} />
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}
